import { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router';

import RootLayout from './root-layout';
import HomePage from '@/pages/home';
import SpacesPage from '@/pages/spaces';
import SpaceDetailPage from '@/pages/space-detail';
import SpaceProposalPage from '@/pages/space-proposal';
import SpaceBookPage from '@/pages/space-book';
import BookPage from '@/pages/book';
import BookingFeedbackPage from '@/pages/booking-feedback';
import EventsPage from '@/pages/events';
import EventDetailPage from '@/pages/event-detail';
import EventRegisterPage from '@/pages/event-register';
import EventFeedbackPage from '@/pages/event-feedback';
import AboutPage from '@/pages/about';
import ContactPage from '@/pages/contact';
import CareersPage from '@/pages/careers';
import JoinPage from '@/pages/join';
import ComponentsPage from '@/pages/components';
import NotFoundPage from '@/pages/not-found';
import { LoadingBlock } from '@/components/states';

/*
 * The café menu loads on its own, the first time someone opens it: its drawn
 * illustrations and the barista game are the heaviest code in the app, and
 * most visitors never go there.
 */
const CafeMenuPage = lazy(() => import('@/pages/cafe-menu'));

/** The client route table. Every page renders inside RootLayout. */
export default function AppRoutes() {
  return (
    <Routes>
      <Route element={<RootLayout />}>
        <Route index element={<HomePage />} />

        <Route path="spaces" element={<SpacesPage />} />
        {/* The café hall has no page of its own: it is a space, and
            `/spaces/cafe` is the same detail screen with its own row. The
            design drew a bespoke one; the difference was the hall's four
            layouts, which are just its packages. */}
        <Route path="spaces/:slug" element={<SpaceDetailPage />} />
        <Route path="spaces/:slug/proposal" element={<SpaceProposalPage />} />
        <Route path="spaces/:slug/book" element={<SpaceBookPage />} />
        <Route
          path="cafe/menu"
          element={
            <Suspense
              fallback={
                <div className="mx-auto max-w-page px-gutter py-section">
                  <LoadingBlock rows={6} />
                </div>
              }
            >
              <CafeMenuPage />
            </Suspense>
          }
        />
        <Route path="book/daily-desk" element={<BookPage />} />
        {/* The link in the booking feedback email. The backend builds it from
            `PUBLIC_APP_URL`, so this path must not change without it. */}
        <Route path="bookings/feedback/:token" element={<BookingFeedbackPage />} />

        <Route path="events" element={<EventsPage />} />
        {/* The link in the post-event feedback email. The backend builds it
            from `PUBLIC_APP_URL`, so this path must not change without it.
            The static `feedback` segment outranks `:slug` on its own. */}
        <Route path="events/feedback/:token" element={<EventFeedbackPage />} />
        <Route path="events/:slug" element={<EventDetailPage />} />
        <Route path="events/:slug/register" element={<EventRegisterPage />} />

        <Route path="about" element={<AboutPage />} />
        <Route path="contact" element={<ContactPage />} />
        <Route path="careers" element={<CareersPage />} />
        {/* The CRM sign-up form. Reached only through a shared link: no nav
            entry, nothing on the site points here. */}
        <Route path="join" element={<JoinPage />} />

        {/* Living reference for everything importable from @mds/shared. */}
        <Route path="components" element={<ComponentsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
